import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Download } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products`);
        setProducts(response.data.products);
      } catch (error) {
        console.error('Error fetching products:', error);
        // Fallback data
        setProducts([
          {
            id: 'geobar-b',
            name: 'GEObar B',
            type: 'BFRP',
            tagline: 'Basalt Fiber Reinforced Polymer Rebar',
            short_description: 'Volcanic fiber. Zero corrosion. 2× the tensile strength of Grade 60 steel.'
          },
          {
            id: 'geobar-fg',
            name: 'GEObar FG',
            type: 'GFRP',
            tagline: 'Glass Fiber Reinforced Polymer Rebar',
            short_description: 'Proven glass fiber reinforcement. Non-magnetic, non-conductive, specification-ready.'
          },
          {
            id: 'geofabric',
            name: 'GEOfabric',
            type: 'Basalt Geogrid',
            tagline: 'Basalt Fiber Geogrid Reinforcement',
            short_description: 'Basalt geogrid — surface and soil reinforcement that outlasts the pour.'
          },
          {
            id: 'chopped-fiber',
            name: 'GEOfiber',
            type: 'Chopped Basalt Fiber',
            tagline: 'Basalt Fiber Concrete Additive',
            short_description: 'Chopped basalt fiber — crack-free concrete that lasts.'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleDownloadPDF = (productId, productName) => {
    window.open(`${API_URL}/api/products/${productId}/pdf`, '_blank');
  };

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="products-page">
      {/* Hero */}
      <section className="py-24 lg:py-32 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">THE GEO PRODUCT FAMILY</p>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              OUR PRODUCTS
            </h1>
            <p className="text-stone/60 max-w-2xl text-lg">
              Advanced composite reinforcement materials engineered for durability, compliance, and performance in demanding infrastructure applications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-8 h-8 border-2 border-bronze border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="card p-8 flex flex-col h-full"
                  data-testid={`product-card-${product.id}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-mono text-bronze text-xs px-2 py-1 border border-bronze">
                      {product.type}
                    </span>
                  </div>

                  <h2 className="heading-section text-stone text-3xl mb-2">{product.name}</h2>
                  <p className="text-stone/50 text-sm mb-4">{product.tagline}</p>

                  <div className="h-48 bg-gradient-to-br from-bronze/10 to-transparent mb-6 flex items-center justify-center">
                    {product.type === 'Basalt Mesh' ? (
                      <div className="grid grid-cols-4 gap-1 opacity-40">
                        {[...Array(16)].map((_, i) => (
                          <div key={i} className="w-6 h-6 border border-stone/40" />
                        ))}
                      </div>
                    ) : (
                      <div className={`w-32 h-2 ${product.type === 'BFRP' ? 'bg-bronze/60' : 'bg-stone/40'}`} />
                    )}
                  </div>

                  <p className="text-stone/60 text-sm leading-relaxed mb-6 flex-grow">
                    {product.short_description}
                  </p>

                  <div className="flex flex-col gap-3 mt-auto">
                    <Link
                      to={`/products/${product.id}`}
                      className="btn btn-outline text-sm w-full"
                      data-testid={`view-${product.id}`}
                    >
                      View Full Specs
                      <ArrowRight size={16} className="ml-2" />
                    </Link>
                    <button
                      onClick={() => handleDownloadPDF(product.id, product.name)}
                      className="btn btn-primary text-sm w-full"
                      data-testid={`download-${product.id}`}
                    >
                      Download PDF
                      <Download size={16} className="ml-2" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-surface-dark border-t border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-display text-stone text-3xl md:text-4xl mb-6">
              NEED HELP SELECTING A PRODUCT?
            </h2>
            <p className="text-stone/60 max-w-xl mx-auto mb-8">
              Our technical team can help you identify the right composite solution for your specific project requirements.
            </p>
            <Link 
              to="/contact" 
              className="btn btn-primary"
              data-testid="products-cta-btn"
            >
              Request Consultation
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
